import React, { useState, useEffect } from 'react';

const Calendar = ({ 
  onDateSelect, 
  selectedDate, 
  availableSlots = {}, 
  bookedSlots = {}, 
  mode = 'patient' // 'patient' or 'clinic'
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');

  const today = new Date();
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  
  // Get first day of month and number of days
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(year, month + direction, 1));
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const isDateDisabled = (date) => {
    if (mode === 'patient') {
      return date < today;
    }
    return false; // Clinics can schedule any date
  };

  const getDateStatus = (date) => {
    const dateStr = formatDate(date);
    const bookedCount = bookedSlots[dateStr]?.length || 0;
    const availableCount = availableSlots[dateStr]?.length || 0;
    
    if (bookedCount > 0 && availableCount === 0) return 'fully-booked';
    if (bookedCount > 0) return 'partially-booked';
    if (availableCount > 0) return 'available';
    return 'unavailable';
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;
    
    const dateStr = formatDate(date);
    onDateSelect(dateStr);
  };

  const getTimeSlots = (dateStr) => {
    const available = availableSlots[dateStr] || [];
    const booked = bookedSlots[dateStr] || [];
    
    return available.filter(slot => !booked.includes(slot));
  };

  const renderCalendarDays = () => {
    const days = [];
    
    // Add empty cells for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-12"></div>);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = formatDate(date);
      const isDisabled = isDateDisabled(date);
      const isSelected = selectedDate === dateStr;
      const status = getDateStatus(date);
      const isToday = date.toDateString() === today.toDateString();
      
      days.push(
        <div
          key={day}
          onClick={() => handleDateClick(date)}
          className={`h-12 flex items-center justify-center rounded-lg cursor-pointer transition-colors relative ${
            isDisabled 
              ? 'text-gray-300 cursor-not-allowed' 
              : isSelected
              ? 'bg-blue-600 text-white font-semibold'
              : isToday
              ? 'bg-blue-100 text-blue-800 font-semibold'
              : 'hover:bg-gray-100 text-gray-700'
          }`}
        >
          {day}
          {!isDisabled && (
            <div className={`absolute bottom-1 w-2 h-2 rounded-full ${
              status === 'fully-booked' ? 'bg-red-500' :
              status === 'partially-booked' ? 'bg-yellow-500' :
              status === 'available' ? 'bg-green-500' :
              'bg-gray-300'
            }`}></div>
          )}
        </div>
      );
    }
    
    return days;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigateMonth(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <h3 className="text-xl font-semibold text-gray-900">
          {monthNames[month]} {year}
        </h3>
        
        <button
          onClick={() => navigateMonth(1)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Day Labels */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {dayNames.map(day => (
          <div key={day} className="h-8 flex items-center justify-center text-sm font-medium text-gray-500">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 mb-6">
        {renderCalendarDays()}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
          <span className="text-gray-600">Available</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
          <span className="text-gray-600">Partially Booked</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
          <span className="text-gray-600">Fully Booked</span>
        </div>
      </div>

      {/* Time Slots */}
      {selectedDate && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Available Times - {new Date(selectedDate).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </h4>
          
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {getTimeSlots(selectedDate).map(time => (
              <button
                key={time}
                onClick={() => setSelectedTimeSlot(time)}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  selectedTimeSlot === time
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-300'
                }`}
              >
                {time}
              </button>
            ))}
          </div>
          
          {getTimeSlots(selectedDate).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">📅</div>
              <p>No available time slots for this date</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Calendar;